import { useCallback, useState, type ReactElement } from 'react'
import { useTeamCache, useUpdateTeam } from '@front/model'
import { useToastError } from '@hooks'
import { Loader } from '@mantine/core'
import { type PartialTeamData, type TeamData, type TeamLevel } from '@terros/common'
import { cn } from '@util'
import { useTeamDrag } from '../../TeamDragContext'
import { useTeamHierarchyContext } from '../../TeamHierarchyContext'
import { TeamNodeContent } from './Content'

type TeamNodeProps = {
  team: TeamData
}

export const TeamNode = ({ team }: TeamNodeProps): ReactElement => {
  const { collapsedTeams, teams, setViewTeam, canEdit } = useTeamHierarchyContext()
  const { dragState, startDrag, endDrag, setDragOverTeam, canDrop } = useTeamDrag()
  const isDragging = dragState.draggedTeam?.teamId === team.teamId
  const isDragOver = dragState.dragOverTeam?.teamId === team.teamId
  const isValidDropTarget = canDrop(team, null)
  const cache = useTeamCache()
  const { update, isUpdating, error } = useUpdateTeam()
  useToastError(error)

  const onDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>): Promise<void> => {
      e.preventDefault()
      e.stopPropagation()
      const draggedTeam = dragState.draggedTeam
      if (isValidDropTarget && draggedTeam) {
        const newParentId = team.teamId
        const newLevel = (team.level - 1) as TeamLevel
        const teamToUpdate: PartialTeamData = {
          teamId: draggedTeam.teamId,
          parentId: newParentId,
          level: newLevel,
        }

        // optimistic update
        cache.addChildTeam(team.teamId, draggedTeam)
        if (draggedTeam.parentId) {
          cache.removeChildTeam(draggedTeam.parentId, draggedTeam.teamId)
        }
        cache.partialUpdateTeam(teamToUpdate)
        update(teamToUpdate).catch(() => {
          // revert optimistic update
          cache.partialUpdateTeam({ ...draggedTeam })
          if (draggedTeam.parentId) {
            cache.addChildTeam(draggedTeam.parentId, draggedTeam)
          }
          cache.removeChildTeam(team.teamId, draggedTeam.teamId)
        })
      }
      endDrag()
    },
    [isValidDropTarget, dragState.draggedTeam, endDrag, team.teamId, team.level, cache, update]
  )

  const onDragStart = useCallback((): void => {
    const parent = teams.find((t) => t.teamId === team.parentId)
    const highestChildLevel = teams
      .filter((t) => t.parentId === team.teamId)
      .reduce<TeamLevel | undefined>((max, t) => {
        if (max === undefined) return t.level
        return Math.max(max, t.level)
      }, undefined)

    startDrag(team, parent?.level, highestChildLevel)
  }, [teams, startDrag, team])

  const onDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>): void => {
      e.preventDefault()
      e.stopPropagation()
      if (dragState.draggedTeam?.teamId !== team.teamId) setDragOverTeam(team)
    },
    [dragState.draggedTeam?.teamId, team, setDragOverTeam]
  )

  const onDragEnd = useCallback((): void => {
    endDrag()
  }, [endDrag])

  const onDragExit = useCallback((): void => {
    setDragOverTeam(null)
  }, [setDragOverTeam])

  const [isEditing, setIsEditing] = useState(false)

  const draggable = !isEditing && canEdit

  return (
    <div
      data-node-id={team.teamId}
      draggable={draggable}
      className={cn(
        'flex min-w-[150px] max-w-[200px] flex-col items-center justify-center rounded-md border border-neutral-200 p-4 shadow-xs',
        draggable ? 'cursor-grab' : 'cursor-pointer',
        dragState.draggedTeam?.teamId === team.teamId && 'cursor-grabbing',
        collapsedTeams.includes(team.teamId) &&
          'border-neutral-light-text bg-neutral-light hover:bg-neutral-light-hover',
        isDragging && 'opacity-50',
        isDragOver && (isValidDropTarget ? 'border-emerald-filled bg-emerald-light' : 'border-rose-filled')
      )}
      onDragEnd={onDragEnd}
      onDragExit={onDragExit}
      onDragOver={onDragOver}
      onDragStart={onDragStart}
      onDrop={onDrop}
      onClick={(e) => {
        setViewTeam(team.teamId)
      }}
    >
      {isUpdating && dragState.draggedTeam?.teamId === team.teamId ? (
        <Loader />
      ) : (
        <TeamNodeContent isEditing={isEditing} setIsEditing={setIsEditing} team={team} />
      )}
    </div>
  )
}
